import i18n from '../i18n';
import { OPTIMISTIC_MATCH_WINDOW_MS } from './chatConstants';

// ── Текущий пользователь ─────────────────────────────────────────
// В localStorage лежит объект из логина; если id там нет — берём sub из JWT.
export const getStoredAuthUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const ID_FIELDS = ['id', 'userId', 'userProfileId', 'profileId'];

const jwtPayload = () => {
  try {
    const token = localStorage.getItem('accessToken');
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

export const getCurrentUserId = () => {
  const user = getStoredAuthUser();
  const fromUser = ID_FIELDS.map((field) => user?.[field]).find(Boolean);
  if (fromUser) return String(fromUser);

  const payload = jwtPayload();
  const fromToken = payload?.userId || payload?.id || payload?.sub;
  return fromToken ? String(fromToken) : null;
};

/**
 * Все известные идентификаторы текущего пользователя: объект user из
 * /api/auth/login и payload токена отдают id в разных полях, а бэкенд
 * отбивает чат с самим собой 400-й ошибкой. Телефон держим как запасной
 * признак — по нему совпадение однозначное.
 */
export const getCurrentUserIdentity = () => {
  const user = getStoredAuthUser();
  const payload = jwtPayload();
  const ids = new Set();

  ID_FIELDS.forEach((field) => {
    if (user?.[field]) ids.add(String(user[field]));
  });
  [payload?.userId, payload?.id, payload?.sub].forEach((value) => {
    if (value) ids.add(String(value));
  });

  return { ids, phoneNumber: user?.phoneNumber ? String(user.phoneNumber) : null };
};

// Совпадает ли кандидат из списка сотрудников с текущим пользователем.
export const isSelf = (candidate, identity) => {
  if (!candidate || !identity) return false;
  const matchesId = ID_FIELDS.some(
    (field) => candidate[field] && identity.ids.has(String(candidate[field]))
  );
  if (matchesId) return true;
  return Boolean(
    identity.phoneNumber &&
    candidate.phoneNumber &&
    String(candidate.phoneNumber) === identity.phoneNumber
  );
};

// ── Сообщения ────────────────────────────────────────────────────
let tempCounter = 0;
export const makeTempId = () => `tmp-${Date.now()}-${(tempCounter += 1)}`;

export const isOptimistic = (message) => message?.id == null && Boolean(message?.tempId);

const byId = (a, b) => {
  // Оптимистичные (без серверного id) всегда в хвосте.
  if (a.id == null && b.id == null) return String(a.tempId).localeCompare(String(b.tempId));
  if (a.id == null) return 1;
  if (b.id == null) return -1;
  return a.id - b.id;
};

export const sortMessages = (list) => [...list].sort(byId);

/**
 * Дедупликация по id: своё же сообщение прилетает обратно по сокету —
 * это нормально, но задваиваться оно не должно.
 * Дополнительно гасим оптимистичный дубль: сервер не знает про tempId,
 * поэтому матчим по «отправитель + текст + окно в несколько секунд».
 */
export const upsertMessage = (list = [], incoming) => {
  if (!incoming) return list;

  const withoutSame = [];
  let replaced = false;

  for (const message of list) {
    if (incoming.id != null && message.id === incoming.id) {
      withoutSame.push({ ...message, ...incoming });
      replaced = true;
      continue;
    }
    if (
      !replaced &&
      isOptimistic(message) &&
      message.senderId === incoming.senderId &&
      (message.content || '') === (incoming.content || '') &&
      message.messageType === incoming.messageType &&
      Math.abs(new Date(incoming.createdAt) - new Date(message.createdAt)) < OPTIMISTIC_MATCH_WINDOW_MS
    ) {
      withoutSame.push(incoming);
      replaced = true;
      continue;
    }
    withoutSame.push(message);
  }

  if (!replaced) withoutSame.push(incoming);
  return sortMessages(withoutSame);
};

export const mergeMessages = (list = [], incoming = []) =>
  incoming.reduce((acc, message) => upsertMessage(acc, message), list);

export const oldestMessageId = (list = []) => {
  const withId = list.filter((m) => m.id != null);
  return withId.length > 0 ? withId[0].id : null;
};

export const newestMessageId = (list = []) => {
  const withId = list.filter((m) => m.id != null);
  return withId.length > 0 ? withId[withId.length - 1].id : null;
};

// ── Форматирование ───────────────────────────────────────────────
export const dayKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

export const formatTime = (iso, locale = 'ru') =>
  new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

export const formatDayLabel = (iso, locale = 'ru') => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (dayKey(date) === dayKey(today)) return i18n.t('chat.date.today');
  if (dayKey(date) === dayKey(yesterday)) return i18n.t('chat.date.yesterday');
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' });
};

// Дата в списке чатов: сегодня — время, иначе короткая дата.
export const formatListDate = (iso, locale = 'ru') => {
  if (!iso) return '';
  const date = new Date(iso);
  if (dayKey(date) === dayKey(new Date())) return formatTime(iso, locale);
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
};

// ── Участники ────────────────────────────────────────────────────
// Вышедших участников в списке уже нет — показываем нейтральную подпись.
export const unknownParticipant = () => i18n.t('chat.system.unknown');

export const findParticipant = (chat, userId) =>
  (chat?.participants || []).find((p) => String(p.userId) === String(userId)) || null;

export const participantName = (chat, userId) =>
  findParticipant(chat, userId)?.fullName || unknownParticipant();

export const isGroupChat = (chat) => chat?.type === 'GROUP';

export const myParticipantRole = (chat, currentUserId) =>
  findParticipant(chat, currentUserId)?.role || null;

// Добавлять/удалять участников и переименовывать могут только владелец и админ.
export const canManageGroup = (chat, currentUserId) => {
  const role = myParticipantRole(chat, currentUserId);
  return role === 'OWNER' || role === 'ADMIN';
};

// ── Системные сообщения ──────────────────────────────────────────
// В content лежит код (MEMBER_ADDED:<uuid> и т.п.), текст собираем на клиенте.
export const systemMessageText = (message, chat) => {
  const raw = message?.content || '';
  const separator = raw.indexOf(':');
  const code = separator === -1 ? raw : raw.slice(0, separator);
  const argument = separator === -1 ? '' : raw.slice(separator + 1);
  const author = participantName(chat, message?.senderId);

  switch (code) {
    case 'MEMBER_ADDED':
      return i18n.t('chat.system.memberAdded', { author, member: participantName(chat, argument) });
    case 'MEMBER_REMOVED':
      return i18n.t('chat.system.memberRemoved', { author, member: participantName(chat, argument) });
    case 'MEMBER_LEFT':
      return i18n.t('chat.system.memberLeft', { member: participantName(chat, argument) });
    case 'TITLE_CHANGED':
      return i18n.t('chat.system.titleChanged', { author, title: argument });
    default:
      return raw;
  }
};

export const messagePreview = (message, chat) => {
  if (!message) return '';
  if (message.messageType === 'SYSTEM') return systemMessageText(message, chat);

  const body = (() => {
    switch (message.messageType) {
      case 'IMAGE':
        return i18n.t('chat.preview.photo');
      case 'VOICE':
        return i18n.t('chat.preview.voice');
      case 'FILE':
        return message.attachment?.fileName || i18n.t('chat.preview.file');
      default:
        return message.content || '';
    }
  })();

  // В группе превью идёт с именем автора: «Иван: привет».
  if (isGroupChat(chat) && message.senderId) {
    return `${participantName(chat, message.senderId)}: ${body}`;
  }
  return body;
};

export const formatDuration = (ms) => {
  const total = Math.max(0, Math.round((ms || 0) / 1000));
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
};

export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

export const parseWaveform = (waveform) => {
  if (!waveform) return [];
  return String(waveform)
    .split(',')
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));
};

// Заголовок: у группы — title, у личного чата — имя собеседника
// (из participants, из подтянутого профиля или мягкий фолбэк по peerId).
export const chatTitle = (chat) => {
  if (!chat) return '';
  if (isGroupChat(chat)) return chat.title || i18n.t('chat.fallback.group');

  const peer = chat.peerId ? findParticipant(chat, chat.peerId) : null;
  if (peer?.fullName) return peer.fullName;
  if (chat.peerName) return chat.peerName;
  if (chat.type === 'ORDER') return i18n.t('chat.fallback.order', { id: String(chat.id).slice(0, 8) });
  return chat.peerId
    ? i18n.t('chat.fallback.user', { id: String(chat.peerId).slice(0, 8) })
    : i18n.t('chat.fallback.chat');
};

export const initialsOf = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

export const chatInitials = (chat) => {
  if (isGroupChat(chat)) {
    return (chat.title || i18n.t('chat.fallback.group')).trim().charAt(0).toUpperCase();
  }
  return initialsOf(chatTitle(chat));
};

// Аватар собеседника в личном чате (presigned, 30 минут).
export const chatAvatarUrl = (chat) => {
  if (!chat || isGroupChat(chat)) return null;
  return (chat.peerId ? findParticipant(chat, chat.peerId)?.avatar : null) || null;
};

// Для групповой иконки-коллажа берём первых участников.
export const groupCollage = (chat, limit = 3) => (chat?.participants || []).slice(0, limit);

// Закреплённые сверху, дальше по дате последнего сообщения.
export const sortChats = (chats = []) =>
  [...chats].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    const aTime = a.lastMessage ? new Date(a.lastMessage.createdAt).getTime() : 0;
    const bTime = b.lastMessage ? new Date(b.lastMessage.createdAt).getTime() : 0;
    return bTime - aTime;
  });
